// ────────────────────────────────────────────────────────────────────────────
// Settings → Documents.
//
// One section per document kind, each listing the layouts the organisation can
// print with. The default layout is what a new document uses, so it is the
// first thing shown and the only status that carries a badge.
//
// Kept as a list rather than a gallery of thumbnails on purpose: a thumbnail
// of an A4 invoice at card size is unreadable, and rendering one PDF per card
// would mean a dozen react-pdf renders just to open the page. The preview is a
// full-size pane inside the builder instead.
// ────────────────────────────────────────────────────────────────────────────
import { Button, ConfirmDialog, FormSection, PageHeader, cn, showToast } from '@my/ui';
import { Copy, Eye, FileText, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { useDocumentTemplates } from './lib/useDocumentTemplates';
import { useLocalTemplateImport } from './lib/useLocalTemplateImport';
import { DOC_TYPES, DOC_TYPE_LABEL, type DocumentTemplate, type DocumentType } from './lib/types';

/** Default first, then the shipped baseline last, then by name. */
function sortTemplates(items: DocumentTemplate[]): DocumentTemplate[] {
  return [...items].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    if (a.isStock !== b.isStock) return a.isStock ? 1 : -1;
    return a.name.localeCompare(b.name);
  });
}

function formatUpdated(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB');
}

function TemplateRow({
  template,
  onEdit,
  onDuplicate,
  onSetDefault,
  onDelete,
}: {
  template: DocumentTemplate;
  onEdit: () => void;
  onDuplicate: () => void;
  onSetDefault: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-lg border bg-white px-3 py-2',
        template.isDefault ? 'border-amber-200' : 'border-slate-200',
      )}
    >
      <FileText className="h-4 w-4 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-slate-800">{template.name}</p>
          {template.isDefault && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200">
              <Star className="h-3 w-3" />
              Default
            </span>
          )}
          {template.isStock && (
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
              Supplied by Stratum
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-400">
          {template.isStock
            ? 'Read-only baseline'
            : `Updated ${formatUpdated(template.updatedUtc)}`}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {!template.isDefault && (
          <Button size="xs" variant="ghost" leftIcon={Star} onClick={onSetDefault}>
            {/* Set default */}
          </Button>
        )}
        <Button size="xs" variant="ghost" leftIcon={Copy} onClick={onDuplicate}>
          {/* Duplicate */}
        </Button>
        <Button
          size="xs"
          variant="ghost"
          leftIcon={template.isStock ? Eye : Pencil}
          onClick={onEdit}
        >
          {/* {template.isStock ? 'View' : 'Edit'} */}
        </Button>
        {!template.isStock && (
          <Button
            size="xs"
            variant="ghost"
            icon={Trash2}
            ariaLabel={`Delete ${template.name}`}
            onClick={onDelete}
          />
        )}
      </div>
    </div>
  );
}

export function DocumentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const organisationId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const {
    templates,
    isLoading,
    error,
    isSaving,
    createTemplate,
    createFromLayout,
    duplicateTemplate,
    setDefault,
    deleteTemplate,
  } = useDocumentTemplates(organisationId);

  // Lift anything designed before layouts were stored server-side. Gated on a
  // *loaded* empty list — running while the query is pending would import over
  // layouts that simply had not arrived.
  const savedCount = templates.filter((t) => !t.isStock).length;
  const { imported } = useLocalTemplateImport(
    organisationId,
    !isLoading && !error && savedCount === 0,
    createFromLayout,
  );

  const [pendingDelete, setPendingDelete] = useState<DocumentTemplate | null>(null);

  const handleCreate = async (docType: DocumentType) => {
    // Named on creation and renamed in the builder — one hop instead of a
    // name-first modal the operator has to fill in before seeing anything.
    const id = await createTemplate(docType, `${DOC_TYPE_LABEL[docType]} layout`);
    if (id) navigate(`/settings/documents/${id}`);
  };

  const handleDuplicate = async (template: DocumentTemplate) => {
    const id = await duplicateTemplate(template.id);
    if (id) navigate(`/settings/documents/${id}`);
  };

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title="Documents"
        description="Design the layouts your organisation prints invoices and credit notes with."
      />

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {imported.length > 0 && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
            Moved {imported.length === 1 ? 'your layout' : `${imported.length} layouts`} into this
            organisation: {imported.join(', ')}. They were saved in this browser only and are now
            shared with everyone here.
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            Could not load your layouts — {error.message}
          </div>
        )}

        {DOC_TYPES.map((docType) => {
          const forType = sortTemplates(templates.filter((t) => t.docType === docType));
          return (
            <FormSection
              key={docType}
              title={`${DOC_TYPE_LABEL[docType]} layouts`}
              icon={FileText}
              columns={1}
              actions={
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={Plus}
                  disabled={isSaving || !organisationId}
                  onClick={() => void handleCreate(docType)}
                >
                  New layout
                </Button>
              }
            >
              <div className="space-y-2">
                {forType.map((template) => (
                  <TemplateRow
                    key={template.id}
                    template={template}
                    onEdit={() => navigate(`/settings/documents/${template.id}`)}
                    onDuplicate={() => void handleDuplicate(template)}
                    onSetDefault={() => {
                      void setDefault(template.id).then(() =>
                        showToast({
                          message: `${template.name} is now the default ${DOC_TYPE_LABEL[
                            docType
                          ].toLowerCase()} layout`,
                          type: 'success',
                        }),
                      );
                    }}
                    onDelete={() => setPendingDelete(template)}
                  />
                ))}
              </div>
            </FormSection>
          );
        })}
      </main>

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={`Delete ${pendingDelete?.name ?? 'this layout'}?`}
        message={
          pendingDelete?.isDefault
            ? 'This is the default layout. Deleting it puts the Stratum Standard layout back as the default.'
            : 'Documents already issued keep the copy they were printed with.'
        }
        confirmLabel="Delete"
        onCancel={() => setPendingDelete(null)}
        busy={isSaving}
        onConfirm={() => {
          const target = pendingDelete;
          if (target) {
            void deleteTemplate(target.id).then(() =>
              showToast({ message: `${target.name} deleted`, type: 'success' }),
            );
          }
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
